import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LogIn, LogOut, UserCheck, Clock, Calendar, Building2, BarChart3 } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "Staff", label: "Employees" },
  { key: "Security", label: "Security" },
  { key: "Housekeeping", label: "Housekeeping" },
];

export default function StaffAttendance() {
  const [mode, setMode] = useState("daily");
  const [tab, setTab] = useState("Staff");
  const [staff, setStaff] = useState([]);
  const [records, setRecords] = useState([]);

  const categoryOf = (role) => (role === "Security" ? "Security" : role === "Housekeeping" ? "Housekeeping" : "Staff");

  const fetchAll = useCallback(async () => {
    try {
      const [hr, att] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hr`),
        axios.get(`${BACKEND_URL}/api/staff-attendance?category=${tab}`),
      ]);
      setStaff(hr.data.filter((e) => categoryOf(e.role) === tab));
      setRecords(att.data);
    } catch (e) { console.error(e); }
  }, [tab]);

  useEffect(() => { if (mode === "daily") fetchAll(); }, [fetchAll, mode]);

  const mark = async (staff_id, status) => {
    try {
      await axios.post(`${BACKEND_URL}/api/staff-attendance`, { staff_id, status, category: tab });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || "Could not mark attendance"); }
  };

  const recFor = (id) => records.find((r) => r.staff_id === id);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700" data-testid="staff-attendance-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Staff Attendance</h1>
          <p className="text-text_secondary text-sm">Mark daily check-in/out or view the monthly summary — for employees, security and housekeeping, per branch.</p>
        </div>
        <div className="flex gap-2 bg-card_bg border border-border_soft rounded-xl p-1">
          <button data-testid="mode-daily" onClick={() => setMode("daily")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${mode === "daily" ? "bg-primary text-[#111827]" : "text-text_secondary"}`}>Daily</button>
          <button data-testid="mode-report" onClick={() => setMode("report")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${mode === "report" ? "bg-primary text-[#111827]" : "text-text_secondary"}`}><BarChart3 size={15} /> Monthly Report</button>
        </div>
      </div>

      {mode === "report" ? <MonthlyReport /> : (
      <>
      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`att-tab-${t.key}`} onClick={() => setTab(t.key)} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === t.key ? "bg-primary text-[#111827]" : "bg-card_bg border border-border_soft text-text_secondary hover:text-text_primary"}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-card_bg border border-border_soft rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full text-left whitespace-nowrap">
          <thead className="border-b border-border_soft/50">
            <tr>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Name</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Branch</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Check In</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Check Out</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold text-center">Mark</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border_soft/40">
            {staff.length === 0 && <tr><td colSpan="5" className="px-6 py-8 text-text_secondary text-center">No {TABS.find(t=>t.key===tab).label.toLowerCase()} records for your branch.</td></tr>}
            {staff.map((e) => {
              const r = recFor(e.id);
              return (
                <tr key={e.id} className="hover:bg-card_bg/30 transition-colors">
                  <td className="px-6 py-4"><p className="text-text_primary font-bold text-sm">{e.name}</p><p className="text-xs text-text_secondary">{e.role}</p></td>
                  <td className="px-6 py-4 text-text_secondary text-sm">{e.branch || "—"}</td>
                  <td className="px-6 py-4 text-sm"><span className={r?.check_in ? "text-accent_success font-bold flex items-center gap-1" : "text-text_secondary"}>{r?.check_in ? <><Clock size={13}/> {r.check_in}</> : "—"}</span></td>
                  <td className="px-6 py-4 text-sm"><span className={r?.check_out ? "text-gold font-bold flex items-center gap-1" : "text-text_secondary"}>{r?.check_out ? <><Clock size={13}/> {r.check_out}</> : "—"}</span></td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button data-testid={`checkin-${e.id}`} onClick={() => mark(e.id, "in")} className="p-2.5 rounded-lg bg-accent_success/15 border border-accent_success/40 text-accent_success hover:bg-accent_success hover:text-[#111827] transition-colors" title="Check In"><LogIn size={16} /></button>
                      <button data-testid={`checkout-${e.id}`} onClick={() => mark(e.id, "out")} className="p-2.5 rounded-lg bg-primary/10 border border-primary/30 text-gold hover:bg-primary hover:text-[#111827] transition-colors" title="Check Out"><LogOut size={16} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 text-text_secondary text-xs"><UserCheck size={14} className="text-gold" /> Showing today's attendance.</div>
      </>
      )}
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MonthlyReport = () => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [branch, setBranch] = useState("all");
  const [branches, setBranches] = useState([]);
  const [showBranch, setShowBranch] = useState(false);
  const [data, setData] = useState({ rows: [], summary: {}, total_days: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [me, br] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`),
          axios.get(`${BACKEND_URL}/api/branches`),
        ]);
        setShowBranch(rank(me.data.user.role) >= 80);
        setBranches(br.data || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/staff-attendance/report?month=${month}&branch=${branch}`);
      setData(r.data);
    } catch (e) { console.error(e); }
  }, [month, branch]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const summaryCards = [
    { key: "Staff", label: "Employees" },
    { key: "Security", label: "Security" },
    { key: "Housekeeping", label: "Housekeeping" },
  ];

  return (
    <div className="space-y-6" data-testid="monthly-report">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-card_bg border border-border_soft rounded-xl px-3 py-2.5">
          <Calendar size={16} className="text-gold" />
          <input type="month" data-testid="report-month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-transparent text-text_primary text-sm font-semibold outline-none" />
        </div>
        {showBranch && (
          <div className="flex items-center gap-2 bg-card_bg border border-border_soft rounded-xl px-3 py-2.5">
            <Building2 size={16} className="text-gold" />
            <select data-testid="report-branch" value={branch} onChange={(e) => setBranch(e.target.value)} className="bg-transparent text-text_primary text-sm font-semibold outline-none cursor-pointer">
              <option value="all">All Branches</option>
              {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
        )}
        <span className="text-text_secondary text-xs">{data.total_days} days in month</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summaryCards.map((c) => {
          const s = data.summary?.[c.key] || { headcount: 0, avg_present: 0 };
          return (
            <div key={c.key} data-testid={`summary-${c.key}`} className="bg-card_bg border border-border_soft rounded-xl p-5">
              <h3 className="text-[11px] font-semibold text-text_secondary uppercase tracking-wider mb-3">{c.label}</h3>
              <div className="flex items-end justify-between">
                <div><p className="text-3xl font-bold text-text_primary">{s.headcount}</p><p className="text-xs text-text_secondary mt-1">on roll</p></div>
                <div className="text-right"><p className="text-2xl font-bold text-gold">{s.avg_present}</p><p className="text-xs text-text_secondary mt-1">avg days present</p></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-card_bg border border-border_soft rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full text-left whitespace-nowrap">
          <thead className="border-b border-border_soft/50">
            <tr>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Name</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Category</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Branch</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold text-center">Present Days</th>
              <th className="px-6 py-4 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Attendance %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border_soft/40">
            {(data.rows || []).length === 0 && <tr><td colSpan="5" className="px-6 py-8 text-text_secondary text-center">No staff found for this selection.</td></tr>}
            {(data.rows || []).map((r) => (
              <tr key={r.staff_id} className="hover:bg-card_bg/30 transition-colors">
                <td className="px-6 py-4"><p className="text-text_primary font-bold text-sm">{r.staff_name}</p><p className="text-xs text-text_secondary">{r.role}</p></td>
                <td className="px-6 py-4 text-text_secondary text-sm">{r.category}</td>
                <td className="px-6 py-4 text-text_secondary text-sm">{r.branch}</td>
                <td className="px-6 py-4 text-center text-text_primary font-bold text-sm">{r.present_days} <span className="text-text_secondary font-normal">/ {r.total_days}</span></td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-main_bg rounded-full overflow-hidden max-w-[140px]"><div className="h-full bg-gold" style={{ width: `${r.percent}%` }} /></div>
                    <span className="text-xs font-bold text-gold w-9">{r.percent}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
